import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { fetchSeoProjectOptions } from '@/data/sources';
import { fetchSeoBacklinksLite } from '@/data/sources-seoc';
import type { BacklinkRow, BacklinksLiteResult } from '@/types/seoc';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/backlinks — Backlinks (Beta, W3a). Read-only, source-attributed backlink
 * sample for the project's domain, from DataForSEO Backlinks.
 *
 * HONESTY + SAFETY INVARIANTS (W3a contract):
 *  - BETA + VENDOR-FRAGILE: the envelope carries beta:true + vendor_fragile:true.
 *    A prominent banner tells the user this dataset is beta and can be patchy —
 *    we never present it as authoritative.
 *  - PAID (DataForSEO Backlinks): NEVER auto-fires. Project options is a free
 *    list read; the backlinks run ONLY on an explicit click. No useQuery is
 *    wired to the paid read, so mounting this page charges nothing.
 *  - Source-attributed (source + provider fetched_at + cache indicator); every
 *    null metric renders as an em-dash — numbers are NEVER invented.
 *  - Results render in the shared SortableTable (client-side sort only — free).
 *  - Honest states: idle / loading / error / not-configured (503).
 */

const ROW_LIMIT = 50;

function sourceLabel(source: string): string {
  if (source.startsWith('dataforseo.backlinks')) return 'DataForSEO Backlinks';
  if (source.startsWith('dataforseo')) return 'DataForSEO';
  return source;
}

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** em-dash for any null provider metric — never a fabricated value. */
function orInt(v: number | null): string {
  return v === null ? '—' : formatInt(v);
}

interface Panel {
  title: string;
  body: string;
}

type SectionState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; panel: Panel }
  | { status: 'done'; data: T };

function describeError(err: unknown): Panel {
  if (err instanceof ApiError) {
    if (err.status === 503 || err.code === 'provider_unavailable') {
      return {
        title: 'Backlinks isn’t connected yet',
        body: 'Live backlink data needs the DataForSEO Backlinks connection, which isn’t configured for this environment yet. Nothing was fetched and nothing was charged — an honest “not configured” state, never placeholder data.',
      };
    }
    if (err.status === 402 || err.code === 'budget_exceeded') {
      return {
        title: 'Monthly provider budget reached',
        body: 'This lookup would exceed your workspace’s monthly provider budget, so it was refused before any provider call was made — nothing was spent. It resumes when the budget resets, or after you raise the cap on Spend & Budget.',
      };
    }
    if (err.status === 429 || err.code === 'rate_limited') {
      return {
        title: 'Provider rate limit reached',
        body: 'This workspace has hit the provider rate limit, so nothing was fetched or charged. Try again as the window rolls over.',
      };
    }
    if (err.status === 502 || err.code === 'provider_error') {
      return {
        title: 'The provider request failed',
        body: `${err.message ? `${err.message} ` : ''}Backlinks data is vendor-fragile and can fail intermittently — nothing was stored or charged. Try again in a moment.`,
      };
    }
    if (err.status === 404) {
      return {
        title: 'Project not found',
        body: 'This SEO project isn’t available to your workspace. Pick another project and try again.',
      };
    }
    return {
      title: 'The lookup could not be completed',
      body: err.message || 'The data service returned an unexpected response. Nothing was charged — try again.',
    };
  }
  return {
    title: 'The lookup could not be completed',
    body: err instanceof Error ? err.message : 'Unexpected error — nothing was charged. Try again.',
  };
}

function SourceFooter({
  source,
  fetchedAt,
  cacheHit,
}: {
  source: string;
  fetchedAt: string | null;
  cacheHit: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
      <Badge tone="neutral">Source: {sourceLabel(source)}</Badge>
      <Badge tone={cacheHit ? 'neutral' : 'success'}>
        {cacheHit ? 'Served from cache' : 'Live result'}
      </Badge>
      <span>{fetchedAt ? `Last updated ${fetchedAt}` : 'Update time not reported'}</span>
    </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border-subtle bg-canvas px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{label}</div>
      <div className="mt-1 font-mono text-lg text-text-primary">{value}</div>
    </div>
  );
}

const BACKLINK_COLUMNS: SortableColumn<BacklinkRow>[] = [
  {
    key: 'source_domain',
    header: 'Linking domain',
    sortable: true,
    sortValue: (b) => b.source_domain,
    render: (b) => b.source_domain,
    truncate: true,
    maxWidthClass: 'max-w-[220px]',
    title: (b) => b.source_url || b.source_domain,
    cellClassName: 'font-medium text-text-primary',
  },
  {
    key: 'target_url',
    header: 'Target',
    render: (b) => b.target_url || '—',
    truncate: true,
    maxWidthClass: 'max-w-[240px]',
    title: (b) => b.target_url || undefined,
    cellClassName: 'text-text-tertiary',
  },
  {
    key: 'anchor',
    header: 'Anchor',
    render: (b) => (b.anchor === '' ? '—' : b.anchor),
    truncate: true,
    maxWidthClass: 'max-w-[200px]',
    title: (b) => (b.anchor === '' ? undefined : b.anchor),
    cellClassName: 'text-text-secondary',
  },
  {
    key: 'dofollow',
    header: 'Type',
    sortable: true,
    sortValue: (b) => (b.dofollow ? 'dofollow' : 'nofollow'),
    render: (b) => (
      <Badge tone={b.dofollow ? 'success' : 'neutral'}>{b.dofollow ? 'dofollow' : 'nofollow'}</Badge>
    ),
    cellClassName: 'text-text-tertiary',
  },
  {
    key: 'first_seen',
    header: 'First seen',
    sortable: true,
    sortValue: (b) => (b.first_seen === '' ? null : b.first_seen),
    render: (b) => (b.first_seen === '' ? '—' : b.first_seen),
    cellClassName: 'font-mono text-text-secondary',
  },
  {
    key: 'rank',
    header: 'Rank',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (b) => b.rank,
    render: (b) => orInt(b.rank),
    cellClassName: 'font-mono text-text-secondary',
  },
];

export function SeoBacklinksPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const navigate = useNavigate();

  // Free list read — safe on mount. The paid backlinks are never wired to a
  // query; they run on click only.
  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();

  const projectId = resolveActiveProjectId(active, projects);

  const [backlinks, setBacklinks] = useState<SectionState<BacklinksLiteResult>>({ status: 'idle' });

  const runBacklinks = async () => {
    if (projectId === null || backlinks.status === 'loading') return;
    setBacklinks({ status: 'loading' });
    try {
      const data = await fetchSeoBacklinksLite(projectId, ROW_LIMIT);
      setBacklinks({ status: 'done', data });
    } catch (err) {
      setBacklinks({ status: 'error', panel: describeError(err) });
    }
  };

  const onProjectChange = (id: string) => {
    void select(Number(id));
    setBacklinks({ status: 'idle' });
  };

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Backlinks" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Backlinks" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }
  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Backlinks" />
        <EmptyState
          title="No project to explore yet"
          teach="Backlinks runs against an SEO project. Add your first domain on the Projects page, then come back here to see who links to it."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const inputClass =
    'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => onProjectChange(e.target.value)}
        className={inputClass}
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Backlinks"
        subtitle="A sample of who links to your domain, from licensed DataForSEO Backlinks data — loaded on demand, source-attributed, and honest about every missing metric."
        actions={projectSelector}
      />

      {/* BETA + VENDOR-FRAGILE banner — prominent and honest per the W3a contract. */}
      <div
        role="note"
        className="rounded-md border border-warning/50 bg-warning/10 px-4 py-3 text-sm text-text-secondary"
      >
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="warning">Beta</Badge>
          <Badge tone="warning">Vendor-fragile</Badge>
          <span className="font-medium text-text-primary">Treat this data as indicative, not complete.</span>
        </div>
        <p className="mt-1.5">
          Backlink coverage comes from a third-party index that is patchy and can change between
          fetches — counts may be incomplete, and links can appear or disappear. Use it to spot
          trends, not as an authoritative ledger. It is read-only.
        </p>
      </div>

      {!canManage && (
        <div role="status" className="rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
          Backlinks uses paid provider data, so it’s limited to workspace owners and operators.
        </div>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="engine">DataForSEO Backlinks</Badge>
          <span className="text-sm font-medium text-text-primary">Backlink sample</span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-text-tertiary">
              Paid · budget-guarded · 7-day cache · top {ROW_LIMIT}
            </span>
          </div>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Fetch a sample of referring pages with anchor text, follow type and provider rank. Runs
          only when you press the button — nothing is fetched or charged on load.
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="primary"
            onClick={() => void runBacklinks()}
            disabled={!canManage || backlinks.status === 'loading'}
            title={canManage ? undefined : 'Owner/operator only — this uses paid provider data'}
          >
            {backlinks.status === 'loading'
              ? 'Loading…'
              : backlinks.status === 'done'
                ? 'Refresh'
                : 'Fetch backlinks'}
          </Button>
        </div>

        <div className="mt-4">
          {backlinks.status === 'idle' && (
            <p className="text-sm text-text-tertiary">
              No data yet — fetch the backlink sample for this project.
            </p>
          )}
          {backlinks.status === 'loading' && <LoadingState rows={4} />}
          {backlinks.status === 'error' && (
            <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
              <div className="text-sm font-medium text-text-primary">{backlinks.panel.title}</div>
              <p className="mt-1 text-sm text-text-secondary">{backlinks.panel.body}</p>
            </div>
          )}
          {backlinks.status === 'done' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 sm:max-w-md">
                <SummaryTile label="Total backlinks" value={orInt(backlinks.data.summary.total_backlinks)} />
                <SummaryTile label="Referring domains" value={orInt(backlinks.data.summary.referring_domains)} />
              </div>
              {backlinks.data.backlinks.length === 0 ? (
                <div className="rounded-md border border-border-subtle bg-canvas p-3">
                  <p className="text-sm text-text-secondary">
                    The provider returned no backlinks for this domain — an honest empty result, not
                    an error (and expected for newer domains).
                  </p>
                  <div className="mt-2">
                    <SourceFooter
                      source={backlinks.data.source}
                      fetchedAt={backlinks.data.fetched_at}
                      cacheHit={backlinks.data.cache_hit}
                    />
                  </div>
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
                  <SortableTable
                    rows={backlinks.data.backlinks}
                    columns={BACKLINK_COLUMNS}
                    getRowKey={(b, i) => `${b.source_domain}-${i}`}
                    minWidthClass="min-w-[820px]"
                    caption="Backlink sample with anchor text, follow type and provider rank"
                  />
                  <SourceFooter
                    source={backlinks.data.source}
                    fetchedAt={backlinks.data.fetched_at}
                    cacheHit={backlinks.data.cache_hit}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
